// Generated by Copilot- Start
/**
 * ============================================================================
 * PACKAGES.TS - Package metadata, preparation, and availability
 * ============================================================================
 *
 * This file consolidates all package-related operations:
 * - Package metadata collection and summaries
 * - Package preparation and CTA injection
 * - Availability text and SVOD promo handling
 *
 * Replaces: packages.ts, package-preparation.ts, availability-helpers.ts
 */

import { PackageSummary, ChannelAvailability } from "./types";
import { logAdapterEvent } from "../logger";

const HELPER_NAME = "channel-interactive-tool-v2/packages";

// ============================================================================
// SECTION 1: PACKAGE SUMMARIES - SINGLE SOURCE OF TRUTH
// ============================================================================

type VariantType = 'genre-variant' | 'single-package-view' | 'internet' | 'satellite';

interface BuildPackageSummariesArgs {
  packageConfig: any[];
  chnlSrc: string;
  displayGenrePack?: boolean;
  displayGenrePackSatellite?: boolean;
  singlePackageView?: boolean;
  singlePackageConfig?: any;
  showGenrePackages?: boolean;
}

/**
 * Detect which variant we're building for
 */
const detectVariant = (args: BuildPackageSummariesArgs): VariantType => {
  // Genre variant has highest priority (no package summaries needed)
  if (args.showGenrePackages) {
    return 'genre-variant';
  }

  // Single package view
  if (args.singlePackageView) {
    return 'single-package-view';
  }

  // Internet vs Satellite
  if (args.chnlSrc === 'dtvClup') {
    return 'satellite';
  }

  return 'internet';
};

/**
 * Build CTA object from packageConfig.cta
 */
const buildCtaObject = (cta: any): { cta: string; ctaUrl: string; ctaAccessibility?: string } | null => {
  if (!cta) {
    return null;
  }

  return {
    cta: cta.label || cta.cta || '',
    ctaUrl: cta.targeturl || cta.ctaUrl || '',
    ctaAccessibility: cta.accessibility || cta.arialabel || cta.ctaAccessibility
  };
};

/**
 * Build package summary from packageConfig entry
 */
const buildPackageSummaryFromConfig = (pkg: any): PackageSummary => {
  let genrePackageLegal: string | undefined;

  if (pkg.type === 'genre') {
    genrePackageLegal = 'Included in Base pack';
  } else if (pkg.type === 'genre-mini') {
    genrePackageLegal = 'Included in mini pack, requires qualifying base pack';
  }

  return {
    id: pkg.id,
    displayLabel: pkg.displayLabel || pkg.id,
    type: pkg.type,
    order: pkg.order, // Don't add fallback - let modal-package-builder detect missing order
    queryParam: pkg.queryparam || pkg.queryParam || '',
    genrePackageLegal,
    ctaObj: buildCtaObject(pkg.cta)
  };
};

/**
 * Build package summaries for Single Package View variant
 * Note: We ALWAYS build package summaries even if modals are disabled
 * because RSN/Local modals and SVOD tiles still need package data
 */
const buildSinglePackageSummaries = (args: BuildPackageSummariesArgs): Record<string, PackageSummary> => {
  const { packageConfig, singlePackageConfig, chnlSrc, displayGenrePack, displayGenrePackSatellite } = args;

  // Find the single package by ID
  const packageId = singlePackageConfig?.packageId;
  if (!packageId) {
    return {};
  }

  const pkg = packageConfig.find(p => p.id === packageId);
  if (!pkg) {
    return {};
  }

  const summaries: Record<string, PackageSummary> = {};

  // Add the main package
  const summary = buildPackageSummaryFromConfig(pkg);
  summaries[summary.id] = summary;

  // For single package view, also include genre packages if enabled
  // This is needed for RSN/Local modals and SVOD tiles
  const isSatellite = chnlSrc === 'dtvClup';
  const genreEnabled = isSatellite ? displayGenrePackSatellite : displayGenrePack;

  if (genreEnabled) {
    packageConfig.forEach(p => {
      if (p.type === 'genre' || p.type === 'genre-mini') {
        const genreSummary = buildPackageSummaryFromConfig(p);
        summaries[genreSummary.id] = genreSummary;
      }
    });
  }

  return summaries;
};

/**
 * Build package summaries for Internet variant
 */
const buildInternetPackageSummaries = (args: BuildPackageSummariesArgs): Record<string, PackageSummary> => {
  const { packageConfig, displayGenrePack } = args;
  const summaries: Record<string, PackageSummary> = {};

  packageConfig.forEach(pkg => {
    // Include internet packages
    if (pkg.type === 'internet') {
      const summary = buildPackageSummaryFromConfig(pkg);
      summaries[summary.id] = summary;
    }

    // Include genre packages if enabled
    if (displayGenrePack && (pkg.type === 'genre' || pkg.type === 'genre-mini')) {
      const summary = buildPackageSummaryFromConfig(pkg);
      summaries[summary.id] = summary;
    }
  });

  return summaries;
};

/**
 * Build package summaries for Satellite variant
 */
const buildSatellitePackageSummaries = (args: BuildPackageSummariesArgs): Record<string, PackageSummary> => {
  const { packageConfig, displayGenrePackSatellite } = args;
  const summaries: Record<string, PackageSummary> = {};

  packageConfig.forEach(pkg => {
    // Include satellite packages
    if (pkg.type === 'satellite') {
      const summary = buildPackageSummaryFromConfig(pkg);
      summaries[summary.id] = summary;
    }

    // Include genre packages if enabled
    if (displayGenrePackSatellite && (pkg.type === 'genre' || pkg.type === 'genre-mini')) {
      const summary = buildPackageSummaryFromConfig(pkg);
      summaries[summary.id] = summary;
    }
  });

  return summaries;
};

/**
 * Build package summaries for Genre variant
 * Genre variant only needs genre packages for SVODs and RSN/Local modals
 */
const buildGenrePackageSummaries = (args: BuildPackageSummariesArgs): Record<string, PackageSummary> => {
  const { packageConfig } = args;
  const summaries: Record<string, PackageSummary> = {};

  packageConfig.forEach(pkg => {
    // Only include genre and genre-mini packages
    if (pkg.type === 'genre' || pkg.type === 'genre-mini') {
      const summary = buildPackageSummaryFromConfig(pkg);
      summaries[summary.id] = summary;
    }
  });

  return summaries;
};

/**
 * Build package summaries from packageConfig in ONE pass
 * Handles all variant-specific filtering
 * Single source of truth - no more legacy CMS data dependencies
 *
 * @param packageConfig - Full package config array from CMS
 * @param chnlSrc - "streamclup" (internet) or "dtvClup" (satellite)
 * @param displayGenrePack - Enable genre for internet
 * @param displayGenrePackSatellite - Enable genre for satellite
 * @param singlePackageView - Single package view flag
 * @param singlePackageConfig - Single package config object
 * @param showGenrePackages - Genre variant flag (disables package summaries)
 * @returns Package summaries keyed by package ID
 */
export const buildPackageSummaries = (args: BuildPackageSummariesArgs): Record<string, PackageSummary> => {
  try {
    if (!args.packageConfig || args.packageConfig.length === 0) {
      logAdapterEvent(HELPER_NAME, 'buildPackageSummaries: empty packageConfig', {});
      return {};
    }

    const variant = detectVariant(args);

    logAdapterEvent(HELPER_NAME, 'buildPackageSummaries', {
      variant,
      packageConfigCount: args.packageConfig.length,
      chnlSrc: args.chnlSrc
    });

    switch (variant) {
      case 'genre-variant':
        // Genre variant - only genre packages needed for SVODs and RSN/Local modals
        return buildGenrePackageSummaries(args);

      case 'single-package-view':
        return buildSinglePackageSummaries(args);

      case 'internet':
        return buildInternetPackageSummaries(args);

      case 'satellite':
        return buildSatellitePackageSummaries(args);

      default:
        return {};
    }
  } catch (error) {
    console.error(`[${HELPER_NAME}] Error building package summaries:`, error);
    return {};
  }
};

// ============================================================================
// SECTION 2: AVAILABILITY & SVOD PROMO HELPERS
// ============================================================================

/**
 * Normalizes a string by removing HTML tags, diacritics and special characters
 * Matches V1 logic for consistent channel name matching
 * @param str - String to normalize
 * @returns Normalized string (lowercase, alphanumeric only)
 */
export const normalizeString = (str: string): string => {
  if (!str) {
    return '';
  }
  // Trim leading/trailing whitespace first
  const trimmed = str.trim();
  // Strip HTML tags
  const withoutHtml = trimmed.replace(/<[^>]*>/g, '');
  // Remove diacritics
  const normalized = withoutHtml.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  // Remove all non-alphanumeric characters and convert to lowercase
  return normalized.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
};

/**
 * Gets SVOD promo text for a channel if available
 * Checks both svodDetails (main offers) and svodPcpDetails (PCP feed)
 * Priority: included > SVOD promo > addon > promo
 *
 * @param channelName - Channel name to check
 * @param packageBillingCodeLabel - Package billing code (e.g., "entertainment", "choice")
 * @param availability - Current availability type
 * @param svodDetails - Main SVOD offers data (getOffersSvodData or getOffersDTVsSvodData)
 * @param svodPcpDetails - PCP SVOD feed data (svodDtviPcp or svodDtvsPcp)
 * @param chnlSrc - Channel source ("streamclup" or "dtvClup")
 * @returns SVOD promo text or null if not found
 */
export const getSvodPromoText = (
  channelName: string,
  packageBillingCodeLabel: string,
  _availability: ChannelAvailability,
  svodDetails?: any,
  svodPcpDetails?: any[],
  chnlSrc?: string
): string | null => {
  // Check SVOD promo for all channels (including "included" ones)
  // Some channels marked as "included" may still have promo text like "Get DIRECTV Sports Pack..."

  if (!svodDetails || !packageBillingCodeLabel) {
    return null;
  }

  const normalizedChannelName = normalizeString(channelName);
  const normalizedBillingCode = normalizeString(packageBillingCodeLabel);

  // Check svodDetails (main offers data)
  const svodData = svodDetails[normalizedBillingCode];

  if (svodData && Array.isArray(svodData)) {
    for (const offer of svodData) {
      // Check if channel is in the offer's channelList
      const normalizedChannelList = offer.channelList?.map((ch: string) => normalizeString(ch)) || [];

      // Check if channel name is found anywhere in the list (using includes for flexible matching)
      const matchesChannelList = normalizedChannelList.some(ch => ch.includes(normalizedChannelName));
      const matchesChannelName = normalizeString(offer.channelName).includes(normalizedChannelName);

      if (matchesChannelList || matchesChannelName) {
        // Build the promo text
        const monthText = offer.channelDuration > 1 ?
          `${offer.channelDuration} months` :
          `${offer.channelDuration} month`;
        return `Get ${offer.channelName}, ${monthText} included with new service*`;
      }
    }
  }

  // Check svodPcpDetails (PCP feed) as fallback
  if (svodPcpDetails && Array.isArray(svodPcpDetails) && svodData && Array.isArray(svodData)) {
    for (const offer of svodData) {
      // Find matching PCP data by code
      const pcpData = svodPcpDetails.filter(pcp => pcp.code === offer.channelCode);

      for (const pcp of pcpData) {
        const normalizedPcpName = normalizeString(pcp.name);

        // Parse channel list from PCP (may contain HTML for satellite)
        const tempChannelList = pcp.channelList?.flatMap((channel: any) => {
          const channelStr = String(channel);
          if (chnlSrc?.toLowerCase() === "streamclup" && channelStr.includes('<p>')) {
            return channelStr.split(/<\/?p>/).map(normalizeString).filter(Boolean);
          }
          return [normalizeString(channelStr)];
        }) || [];

        // Check if channel name is found anywhere (using includes for flexible matching)
        const matchesPcpName = normalizedPcpName.includes(normalizedChannelName);
        const matchesChannelList = tempChannelList.some(ch => ch.includes(normalizedChannelName));

        if (matchesPcpName || matchesChannelList) {
          const monthText = offer.channelDuration > 1 ?
            `${offer.channelDuration} months` :
            `${offer.channelDuration} month`;
          return `Get ${offer.channelName}, ${monthText} included with new service*`;
        }
      }
    }
  }

  return null;
};

/**
 * Get human-readable availability text based on availability type
 * @param availability - The channel availability type
 * @returns User-friendly text describing package availability
 */
export const getAvailabilityText = (availability: ChannelAvailability): string => {
  switch (availability) {
    case "included":
      return "Included in package";
    case "addon":
      return "Available as an add-on";
    case "promo":
      return "Available as a promo";
    default:
      return "Included in package";
  }
};

/**
 * Get short availability label for UI display
 * @param availability - The channel availability type
 * @returns Short label text
 */
export const getAvailabilityLabel = (availability: ChannelAvailability): string => {
  switch (availability) {
    case "included":
      return "Included";
    case "addon":
      return "Add-on";
    case "promo":
      return "Promo";
    default:
      return "Included";
  }
};
// Generated by Copilot- End
